import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Crown, User, Mail, HardDrive, ArrowUpRight } from "lucide-react";
import PageWrap from "@/fileshare/components/PageWrap";
import { useAuth } from "@/fileshare/context/AuthContext";
import { formatBytes, formatDate } from "@/fileshare/lib/format";
import { Button } from "@/fileshare/components/ui/button";

// Hoisted so React doesn't remount a fresh component on every Settings render.
function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between py-4 border-b border-slate-100 last:border-0">
      <span className="flex items-center gap-2.5 text-sm text-slate-500">
        <Icon className="h-4 w-4 text-slate-400" /> {label}
      </span>
      <span className="text-sm font-medium text-slate-900">{value}</span>
    </div>
  );
}

export default function Settings() {
  const { user, logout } = useAuth();

  return (
    <PageWrap className="max-w-2xl mx-auto px-6 pt-12 pb-24">
      <h1 className="text-2xl font-bold tracking-tight text-slate-900 mb-8">Account settings</h1>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-slate-200 rounded-lg p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
        <h2 className="text-sm font-semibold text-slate-900 mb-2">Profile</h2>
        <Row icon={User} label="Display name" value={user?.display_name} />
        <Row icon={Mail} label="Email" value={user?.email} />
        <Row icon={HardDrive} label="Storage used" value={formatBytes(user?.storage_used || 0)} />
        <Row icon={Crown} label="Plan" value={<span className="capitalize">{user?.tier}</span>} />
      </motion.div>

      {user?.tier !== "business" && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mt-6 bg-blue-50 border border-blue-100 rounded-lg p-6 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Upgrade your plan</h3>
            <p className="text-sm text-slate-600 mt-1">Get bigger uploads, longer retention, and more.</p>
          </div>
          <Link to="/fileshares/pricing"><Button className="bg-blue-500 hover:bg-blue-600 text-white gap-1.5" data-testid="settings-upgrade-btn">Upgrade <ArrowUpRight className="h-4 w-4" /></Button></Link>
        </motion.div>
      )}

      <div className="mt-6">
        <Button data-testid="settings-logout-btn" onClick={logout} variant="outline" className="border-slate-200 text-slate-700">Log out</Button>
      </div>
    </PageWrap>
  );
}
